import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DEFAULT_MENU_STATE } from './ui-preferences.model';
import { UiPreferencesService } from './ui-preferences.service';
import { LayoutService } from '../service/app.layout.service';

/**
 * GAP-UC-43 — Post-login application of saved UI preferences.
 *
 * OQ-CI-3 (RESOLVED — phase 8): the landing redirect is applied ONCE, from the shell
 * (`AppLayoutComponent`) after it initializes, i.e. after authentication has succeeded and
 * AFTER the forced password-change branch. It is invoked only when the user is NOT already
 * deep-linked (the current URL is the app root `/` or empty), so an explicit deep-link
 * navigation is never overridden. It never runs on the `/login` or password-change routes.
 * Landing precedence (INT-02 / REQ-014): per-user preference > roleRouteMappings >
 * DEFAULT_ROUTE 'moji_zadaci'. Here we own only the per-user tier; the existing
 * roleRouteMappings/DEFAULT_ROUTE resolution remains the fallback and is left untouched.
 *
 * OQ-CI-2 (RESOLVED — phase 8): when the saved landing `name` no longer resolves to a
 * navigable item in the user's fresh getAllowedMenus, we do NOT navigate to the missing
 * item and do NOT clear the stored value (it may become valid again after a role change);
 * we simply fall through to the built-in default landing.
 */
@Injectable({ providedIn: 'root' })
export class UiPreferencesLandingService {
  private applied = false;

  constructor(
    private readonly prefs: UiPreferencesService,
    private readonly router: Router,
    private readonly layoutService: LayoutService
  ) {}

  /**
   * Apply saved menu-state + landing exactly once per app load. Safe to call from the shell
   * init; it no-ops on repeat calls and when the user has an explicit deep link (landing only —
   * menu-state applies regardless of route, see below).
   */
  async applyOnce(): Promise<void> {
    if (this.applied) {
      return;
    }

    // Fix 2026-09-23 (bug found via automated regression test, phase 9b): a full browser
    // RELOAD on a deep route (e.g. after the saved landing had already navigated the user away
    // from '/') re-triggers this method with the guard still open, but `isAtAppRoot()` was false
    // for the WHOLE method — so menu-state (a pure visual default, safe to (re)apply on any
    // route) was silently skipped, while only the landing REDIRECT actually needs the root-only
    // restriction (to never override an explicit deep link). Split the two concerns:
    //   - the network-call / spam guard below still prevents load() firing on every in-app
    //     NavigationEnd before the first successful apply (see the 2026-09-23 fix note on the
    //     call site's navToRoot$/combineLatest — every NavigationEnd triggers a call here, not
    //     just root ones), by requiring root-OR-first-ever-attempt before calling load();
    //   - isAtAppRoot() is then re-checked ONLY around the navigateByUrl() call for landing.
    const isRoot = this.isAtAppRoot();
    if (!isRoot && this.attemptedOnce) {
      // Not at root and we've already made our one load() attempt for this app-load cycle
      // (e.g. this is a subsequent in-app navigation, not a fresh page load) -> plain no-op,
      // no network call, guard left open for a genuine next trigger (e.g. next login/reload).
      return;
    }
    this.attemptedOnce = true;

    let prefs;
    try {
      prefs = await firstValueFrom(this.prefs.load());
    } catch {
      // E3/REQ-015: preferences store unavailable -> keep built-in defaults, do not block.
      // Do NOT mark applied: a transient failure should be retried on the next trigger.
      return;
    }

    // We are committed to applying now (prefs loaded): consume the one-shot guard.
    this.applied = true;

    // Menu-state default (REQ-003/012). Only the left menu default is affected (REQ-004/005).
    // Applies on ANY route (including a reload on a deep route) — it only sets a CSS-driving
    // flag, never navigates, so there is no risk of overriding a deep link.
    const menuState = prefs.defaultMenuState ?? DEFAULT_MENU_STATE;
    this.layoutService.state.staticMenuDesktopInactive =
      menuState === 'COLLAPSED';

    if (!prefs.defaultLandingName) {
      // No per-user landing -> fall through to roleRouteMappings/DEFAULT_ROUTE (existing behavior).
      return;
    }

    if (!isRoot) {
      // Landing REDIRECT stays root-only (OQ-CI-3): never override an explicit deep link, and
      // never re-navigate away on a reload of a deep route the landing itself put the user on.
      return;
    }

    // REQ-013: re-resolve the stored name against a FRESH role-filtered menu before navigating.
    let routerLink: string | null;
    try {
      routerLink = await firstValueFrom(
        this.prefs.resolveLanding(prefs.defaultLandingName)
      );
    } catch {
      return; // menu unavailable -> built-in fallback (E3 spirit).
    }

    if (!routerLink) {
      // OQ-CI-2: stale saved landing -> fall back to the built-in default, no navigation error.
      return;
    }

    await this.router.navigateByUrl(routerLink);
  }

  /** True when the user has not deep-linked anywhere (root or empty path). */
  private isAtAppRoot(): boolean {
    const url = this.router.url.split('?')[0].split('#')[0];
    return url === '/' || url === '';
  }

  /**
   * One-shot flag for the load()/network-call attempt within the current app-load cycle
   * (distinct from `applied`, which marks a SUCCESSFUL apply). Reset together with `applied`.
   */
  private attemptedOnce = false;

  /** Reset for a fresh session (e.g. after logout/login within the same page load). */
  reset(): void {
    this.applied = false;
    this.attemptedOnce = false;
  }
}
